import React, { ReactNode, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Color, FontSize, FontWeight, Percent } from 'lib/styles';
import { Background } from 'styles/themeColors';
import { useReadPermission } from 'hooks/useUserRoles';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

// Type definitions (previously from react-tagsinput)
export interface TagProps {
  className?: string;
  [key: string]: any;
}

export interface InputProps {
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

export interface RenderInputProps {
  addTag?: (tag: string) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  ref?: React.Ref<any>;
  [key: string]: any;
}

function defaultRenderTag(props: any): ReactNode {
  const { tag, key, disabled, onRemove, classNameRemove, getTagDisplayValue, ...other } = props;
  return (
    <span key={key} {...other}>
      {getTagDisplayValue(tag)}
      {!disabled && <a className={classNameRemove} onClick={() => onRemove(key)} />}
    </span>
  );
}

function defaultRenderInput({ addTag: _addTag, ref, ...props }: any): ReactNode {
  return <input ref={ref} type="text" {...props} />;
}

interface NativeTagsInputProps {
  className?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  addKeys?: number[];
  onlyUnique?: boolean;
  addOnBlur?: boolean;
  withDelimiter?: boolean;
  inputProps?: InputProps;
  tagProps?: TagProps;
  renderTag?: (props: any) => ReactNode;
  renderInput?: (props: any) => ReactNode;
  disabled?: boolean;
  bgColor?: string;
  color?: string;
}

const NativeTagsInput = React.forwardRef<HTMLDivElement, NativeTagsInputProps>(({
  className,
  value,
  onChange,
  addKeys = [13],
  onlyUnique = true,
  addOnBlur = true,
  inputProps = {},
  tagProps = {},
  renderTag,
  renderInput,
  disabled,
  // consumed by styled-component for CSS only — must not reach the DOM div
  withDelimiter: _withDelimiter,
  bgColor: _bgColor,
  color: _color,
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const getTagDisplayValue = (tag: string) => tag;

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (onlyUnique && value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInputValue('');
  };

  const removeTag = (index: number) => {
    const newTags = [...value];
    newTags.splice(index, 1);
    onChange(newTags);
  };

  const {
    disabled: inputDisabled,
    value: externalValue,
    onChange: externalOnChange,
    onKeyDown: externalOnKeyDown,
    onBlur: externalOnBlur,
    ...restInputProps
  } = inputProps;

  const isDisabled = disabled || inputDisabled;
  // If inputProps.value is provided the input is externally controlled (e.g. autosuggest)
  const displayValue = externalValue !== undefined ? externalValue : inputValue;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.nativeEvent.keyCode;
    if (addKeys!.includes(keyCode)) {
      const tagValue = (e.target as HTMLInputElement).value;
      if (tagValue.trim()) {
        e.preventDefault();
        addTag(tagValue);
      }
    } else if (keyCode === 8 && !displayValue && value.length > 0) {  // Backspace
      // Backspace removes last tag when input is empty
      removeTag(value.length - 1);
    }
    externalOnKeyDown?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (addOnBlur) {
      const tagValue = (e.target as HTMLInputElement).value;
      if (tagValue.trim()) addTag(tagValue);
    }
    externalOnBlur?.(e);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (externalValue === undefined) {
      setInputValue(e.target.value);
    }
    externalOnChange?.(e);
  };

  const tagRenderer = renderTag || defaultRenderTag;
  const inputRenderer = renderInput || defaultRenderInput;

  const { className: tagClassName, ...restTagProps } = tagProps;

  return (
    <div ref={ref} className={className}>
      <span>
        {value.map((tag, index) =>
          tagRenderer({
            key: index,
            index,
            tag,
            disabled: isDisabled,
            onRemove: removeTag,
            classNameRemove: 'react-tagsinput-remove',
            getTagDisplayValue,
            className: tagClassName || 'react-tagsinput-tag',
            ...restTagProps,
          })
        )}
        <div>
          {inputRenderer({
            ref: inputRef,
            value: displayValue,
            onChange: handleInputChange,
            onKeyDown: handleKeyDown,
            onBlur: handleBlur,
            addTag,
            disabled: isDisabled,
            ...restInputProps,
          })}
        </div>
      </span>
    </div>
  );
});

NativeTagsInput.displayName = 'NativeTagsInput';

const StyledTagsInput = styled(NativeTagsInput)<any>`
  width: ${Percent.full};
  background-color: ${props => props.bgColor};
  overflow: hidden;

  .react-tagsinput-tag {
    display: inline-flex;
    font-size: ${FontSize.small};
    font-weight: ${FontWeight.medium};
    margin-bottom: 5px;
    margin-right: 5px;
    margin-left: 5px;
    padding: 5px;
  }

  .react-tagsinput-tag-custom {
    display: inline-flex;
    font-size:  ${FontSize.small};
    font-weight: ${FontWeight.medium};
    border: 1px solid ${Color.lightGray};
    border-radius: 14px;
    background-color: ${Color.carrotOrange};
    margin: 5px;
    padding: 1px 5px;
    color:white;
  }

  span {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    div:after {
      content: ' ';
    }

    div:last-of-type:after {
      content: ' ';
    }
  }

  ${({ withDelimiter }) => withDelimiter && css`
    span {
      div:after {
        content: ', ';
      }

      div:last-of-type:after {
        content: ' ';
      }
    }
  `}

  .react-tagsinput-remove {
    cursor: pointer;
    font-weight: ${FontWeight.bold};
  }

  input[type="text"].react-tagsinput-input {
    background: transparent;
    font-size: ${FontSize.small};
    font-weight: ${FontWeight.medium};
    margin-top: 1px;
    outline: none;
    padding: 10px 5px 15px 5px;
    border: none;
    flex: 1;

    &:focus {
      outline: transparent;
    }
  }

  .react-tagsinput-input-redesign {
    background: transparent;
    font-size: ${FontSize.small};
    font-weight: ${FontWeight.medium};
    margin-top: 1px;
    outline: none;
    padding: 9px 9px;
    border: none;
    width: 180px;

    &:focus {
      outline: transparent;
    }
  }
`;

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  addKeys?: number[];
  onlyUnique?: boolean;
  addOnBlur?: boolean;
  disabled?: boolean;
  withDelimiter?: boolean;
  inputProps?: InputProps;
  tagProps?: TagProps;
  renderTag?: (props: any) => ReactNode;
  renderInput?: (props: RenderInputProps) => React.ReactNode;
  renderInputProps?: RenderInputProps;
  bgColor?: string;
  color?: string;
  permissionCode?: string;
};

const TagsInputComponent = ({
  className = 'react-tagsinput',
  value,
  onChange,
  addKeys = [13],
  onlyUnique = true,
  addOnBlur = true,
  inputProps,
  tagProps,
  renderTag,
  renderInput,
  renderInputProps: _renderInputProps,
  withDelimiter = false,
  bgColor = Background.white,
  color,
  permissionCode,
  ...other
}: Props) => {

  const { readOnly } = useReadPermission(permissionCode!);

  return (
    <ReadOnlyWrapper readOnly={readOnly} className='full-width'>
      <StyledTagsInput
        className={className}
        value={value}
        onChange={onChange}
        addKeys={addKeys}
        onlyUnique={onlyUnique}
        addOnBlur={addOnBlur}
        inputProps={{
          ...inputProps,
          disabled: readOnly
        }}
        tagProps={tagProps}
        renderTag={renderTag}
        renderInput={renderInput}
        withDelimiter={withDelimiter}
        bgColor={bgColor}
        color={color}
        {...other}
      />
    </ReadOnlyWrapper>
  );
};

export default TagsInputComponent;